/**
 * V1 pack hall calls that need the network:
 *
 *   loadHall(siteId, opts)      the hall's pack runs, lots (and buyers, for the hall keeper) into
 *                               the stored site list
 *                               (see hallData.ts) — the site list itself carries none of them
 *   startPackRunV1(run, …)      POST /pack-runs/{id}/start  (F7.2): the pack lot is fixed
 *   dispatchV1(…)               POST /dispatches            (F7.8): a trip of released pouches
 *
 * Bodies are the server's DTOs (camelCase). The demo phone (development builds) answers itself
 * through ../api.ts and keeps its own demo lists.
 */
import { z } from 'zod'
import { api } from '@/api'
import { patchSitePack, readSitePack, packLotSchema, type PackLot, type PackRun } from '@/offline'
import { uploadMedia } from '@/offline/media'
import { httpTransport } from '@/offline/transport'
import { dispatchToBuyer, isDemoPhone, startPackRun } from '../api'
import {
  buyerFromServer,
  keepRun,
  labelFromServer,
  packLotFromServer,
  packRunFromServer,
  previousOnLine,
  rowsOf,
  siteFacts,
  skuFromServer,
  type ServerSku,
} from './hallData'

const get = (path: string, query?: Record<string, string | number | undefined>) =>
  api.get<unknown>(path, { query, timeoutMs: 30_000 })

const loading = new Map<boolean, Promise<boolean>>()

/**
 * Read the hall's lists and keep them with the site list. Each list that cannot be read (no
 * network, or the signed-in account may not read it) keeps what the phone had. True when the
 * stored lists changed. `buyers`: also the customer list, which only the hall keeper's dispatch
 * needs (the line operator may not read it).
 */
export function loadHall(siteId: string, opts: { buyers?: boolean } = {}): Promise<boolean> {
  const buyers = opts.buyers ?? false
  let p = loading.get(buyers)
  if (!p) {
    p = load(siteId, buyers).finally(() => loading.delete(buyers))
    loading.set(buyers, p)
  }
  return p
}

async function load(siteId: string, withBuyers: boolean): Promise<boolean> {
  if (await isDemoPhone()) return false
  const now = Date.now()
  const [runsRes, lotsRes, buyersRes] = await Promise.allSettled([
    get('/pack-runs', { locationId: siteId, limit: 100 }),
    get('/pack-lots', { limit: 100 }),
    withBuyers ? get('/orgs', { kind: 'BUYER' }) : Promise.reject(new Error('not asked')),
  ])
  const runRows = runsRes.status === 'fulfilled' ? rowsOf(runsRes.value) : null
  const lotRows = lotsRes.status === 'fulfilled' ? rowsOf(lotsRes.value) : null
  const buyerRows = buyersRes.status === 'fulfilled' ? rowsOf(buyersRes.value) : null
  if (!runRows && !lotRows && !buyerRows) return false

  // Lots of this hall's runs only (the list endpoint has no place filter).
  const hallRunIds = new Set((runRows ?? []).map((r) => String(r.id)))
  const lots = lotRows
    ? lotRows
        .map(packLotFromServer)
        .filter((l): l is PackLot => !!l && (!runRows || (!!l.run_id && hallRunIds.has(l.run_id))))
    : null

  let runs: PackRun[] | null = null
  if (runRows) {
    const kept = runRows.filter((r) => keepRun(r, now))
    const skuIds = [
      ...new Set(
        runRows
          .map((r) =>
            typeof r.params === 'object' && r.params
              ? (r.params as Record<string, unknown>).sku_id
              : null,
          )
          .filter((x): x is string => typeof x === 'string'),
      ),
    ]
    const skus = new Map<string, ServerSku>()
    const labels = new Map<string, PackRun['label_versions']>()
    await Promise.all(
      skuIds.map(async (id) => {
        const [s, l] = await Promise.allSettled([
          get(`/skus/${encodeURIComponent(id)}`),
          get('/label-versions', { skuId: id, status: 'APPROVED', limit: 20 }),
        ])
        const sku =
          s.status === 'fulfilled' && s.value && typeof s.value === 'object'
            ? skuFromServer(s.value as Record<string, unknown>)
            : null
        if (sku) skus.set(id, sku)
        if (l.status === 'fulfilled')
          labels.set(
            id,
            rowsOf(l.value)
              .map(labelFromServer)
              .filter((x): x is NonNullable<typeof x> => !!x),
          )
      }),
    )
    const stored = await readSitePack()
    const facts = siteFacts(stored?.pack ?? { bags: [], lots: [] })
    const old = new Map((stored?.pack.pack_runs ?? []).map((r) => [r.id, r]))
    const lotByRun = new Map((lots ?? stored?.pack.pack_lots ?? []).map((l) => [l.run_id ?? '', l]))
    const skuOf = (r: Record<string, unknown>) => {
      const p =
        typeof r.params === 'object' && r.params ? (r.params as Record<string, unknown>) : {}
      return typeof p.sku_id === 'string' ? skus.get(p.sku_id) : undefined
    }
    runs = []
    for (const r of kept) {
      const sku = skuOf(r)
      const before = old.get(String(r.id))
      if (!sku) {
        if (before) runs.push(before)
        continue
      }
      const p = previousOnLine(runRows, r)
      const prevSku = p ? skuOf(p) : undefined
      const startedLot = lotByRun.get(String(r.id)) ?? before?.pack_lot ?? null
      const built = packRunFromServer(
        r,
        sku,
        labels.get(sku.id) ?? before?.label_versions ?? [],
        startedLot,
        prevSku ? { crop_id: prevSku.crop_id, crop_name: prevSku.crop_name } : null,
        facts,
      )
      if (built) runs.push(built)
    }
  }

  const buyers = buyerRows
    ? buyerRows
        .map(buyerFromServer)
        .filter((b): b is NonNullable<typeof b> => !!b)
        .map((b) => ({ id: b.id, name: b.name, code: b.code, place: b.place }))
    : null

  await patchSitePack((p) => ({
    ...p,
    pack_runs: runs ?? p.pack_runs,
    pack_lots: lots ?? p.pack_lots,
    buyers: buyers ?? p.buyers,
    site_kind: p.site_kind ?? 'PACKING',
  }))
  return true
}

// ---------------------------------------------------------------------------------------------
// F7.2 Start
// ---------------------------------------------------------------------------------------------

const startResponse = z.looseObject({
  run: z.looseObject({ id: z.string(), code: z.string(), started_at: z.string() }),
  pack_lot: packLotSchema,
})

export interface StartV1 {
  cleanout_photo_id: string
  /** The screen asked for the full clean (the last run on the line was another crop). */
  allergen_clean: boolean
  label_version_id: string
}

/**
 * Start the run (needs network: the server fixes the lot code, dates and QR link). V1 sends no
 * scale check and no last-run leftover (both later; see the alignment map's F7.2 switches).
 */
export async function startPackRunV1(
  run: Pick<PackRun, 'id' | 'code' | 'sku'>,
  body: StartV1,
): Promise<PackLot> {
  if (await isDemoPhone()) {
    const res = await startPackRun(run, {
      cleanout_photo_id: body.cleanout_photo_id,
      allergen_clean_confirmed: body.allergen_clean,
      leftover_g: 0,
      scale_check: { equipment_id: '', reference_g: 500, reading_g: 500 },
      label_version_id: body.label_version_id,
    })
    return res.pack_lot
  }
  // The hopper photo must be on the server before the run can start.
  await uploadMedia(httpTransport.uploadMedia, { ids: [body.cleanout_photo_id], force: true })
  const res = await api.post(
    `/pack-runs/${encodeURIComponent(run.id)}/start`,
    {
      cleanoutPhotoId: body.cleanout_photo_id,
      allergenCleanConfirmed: body.allergen_clean,
      // Not asked in V1 (the red-tie practice stays): nothing typed is nothing found.
      leftoverKg: '0.000',
      labelVersionId: body.label_version_id,
    },
    { schema: startResponse, timeoutMs: 30_000 },
  )
  const lot: PackLot = {
    ...res.pack_lot,
    run_id: run.id,
    sku_id: run.sku.id,
    sku_name: run.sku.name,
    net_g: run.sku.net_g,
  }
  await patchSitePack((p) => ({
    ...p,
    pack_runs: p.pack_runs.map((r) =>
      r.id === run.id
        ? {
            ...r,
            code: res.run.code,
            started_at: res.run.started_at,
            label_version_id: body.label_version_id,
            pack_lot: lot,
          }
        : r,
    ),
    pack_lots: [...p.pack_lots.filter((l) => l.id !== lot.id), lot],
  }))
  return lot
}

// ---------------------------------------------------------------------------------------------
// F7.8 Dispatch
// ---------------------------------------------------------------------------------------------

const dispatchResponse = z.looseObject({ id: z.string(), code: z.string() })

export interface DispatchV1 {
  from_location_id: string
  to_org_id: string
  invoice_no: string
  items: Array<{ pack_lot_id: string; pack_lot_code: string; pouches: number }>
}

/**
 * Record the trip (needs network; never queued — the goods wait until it is saved). V1 sends no
 * vehicle, driver or bed check.
 */
export async function dispatchV1(body: DispatchV1): Promise<{ code: string }> {
  if (await isDemoPhone()) {
    const res = await dispatchToBuyer({
      kind: 'DISPATCH',
      from_location_id: body.from_location_id,
      to_org_id: body.to_org_id,
      challan_no: body.invoice_no,
      vehicle_no: '',
      driver_name: null,
      driver_phone: null,
      vehicle_clean: true,
      items: body.items.map((i) => ({ pack_lot_code: i.pack_lot_code, pouches: i.pouches })),
    })
    return { code: res.code }
  }
  const res = await api.post(
    '/dispatches',
    {
      fromLocationId: body.from_location_id,
      toOrgId: body.to_org_id,
      challanNo: body.invoice_no,
      items: body.items.map((i) => ({ packLotId: i.pack_lot_id, pouches: i.pouches })),
    },
    { schema: dispatchResponse, timeoutMs: 30_000 },
  )
  await patchSitePack((p) => ({
    ...p,
    pack_lots: p.pack_lots.map((l) => {
      const item = body.items.find((i) => i.pack_lot_id === l.id)
      return item ? { ...l, pouches_dispatched: l.pouches_dispatched + item.pouches } : l
    }),
  }))
  return { code: res.code }
}
